/**
 * @file components/common/createDrawer
 * @author zhangzhe(zhangzhe@baidu.com)
 */

import _ from 'lodash';
import classNames from 'classnames';
import React from 'react';
import {createPortal} from 'react-dom';
import {Drawer, Button} from 'acud';

import {ConfigContext} from '../../common/ConfigProvider';
import {DU_PREFIX} from '../../_common/constants';

import './index.less';

const CLS_PREFIX = `${DU_PREFIX}-drawer`;

export interface DrawerProps {
    open?: boolean;
    onSuccess?: (result?: any) => void;
    onCancel?: () => void;
}

export interface DrawerOption {
    className?: string;
    prefixCls?: string;
    title?: string;
    width?: number;
    maskClosable?: boolean;
    cancelWithSuccess?: boolean;
    foot?: boolean;
    okText?: string;
    cancelText?: string;
    payload?: any;
    mask?: boolean; // 是否显示遮罩
    acudProps?: any; // acud属性
    customCancelHandler?: () => Promise<any>;
}

const createDrawer = (
    options: DrawerOption,
    dialogOptionText: any
) => (WrappedComponent?: any): React.ComponentClass => {

    const {
        className = '',
        prefixCls: customizePrefixCls,
        title,
        width,
        maskClosable,
        foot,
        okText,
        cancelText,
        mask = true
    } = options;
    const {
        sure,
        cancel,
        submitting
    } = dialogOptionText;

    const {payload, cancelWithSuccess = false, acudProps, customCancelHandler} = options;

    return class DuDrawer extends React.Component<DrawerProps, any> {

        static getDerivedStateFromProps(nextProps: DrawerProps) {
            if ('open' in nextProps) {
                return {open: nextProps.open};
            }
            return null;
        }

        static contextType = ConfigContext;

        private el: HTMLElement;

        private formRef: any;
        private commonFormRef: any;

        constructor(props: DrawerProps) {
            super(props);

            this.commonFormRef = React.createRef();
            this.el = document.createElement('div');
            this.state = {
                confirmLoading: false,
                open: !!props.open
            };
        }

        componentDidMount() {
            document.body.appendChild(this.el);
        }

        componentWillUnmount() {
            document.body.removeChild(this.el);
        }

        showDrawer = () => {
            this.setState({open: true});
        };

        handleCancel = () => {
            this.setState({open: false});
            const {onCancel, onSuccess} = this.props;
            onCancel && onCancel();
            if (cancelWithSuccess && onSuccess) {
                onSuccess();
            }
        };

        saveRef = (form: any) => {
            this.formRef = form;
        };

        handleOk = () => {
            this.setState({confirmLoading: true});
            let submitFunc: Func | null = null;
            if (this.formRef && this.formRef.doSubmit) {
                submitFunc = this.formRef.doSubmit;
            }
            else if (this.formRef && this.formRef.getWrappedInstance()
                && this.formRef.getWrappedInstance().doSubmit) {
                submitFunc = this.formRef.getWrappedInstance().doSubmit;
            }
            else if (this.commonFormRef && this.commonFormRef.current
                && this.commonFormRef.current.doSubmit) {
                submitFunc = this.commonFormRef.current.doSubmit;
            }

            if (submitFunc) {
                submitFunc()
                    .then(() => this.setState({confirmLoading: false}))
                    .catch(() => this.setState({confirmLoading: false}));
            }
            else {
                // 关闭弹窗
                this.handleCancel();
            }
        };

        handleFormSuccess = (result?: any) => {
            const onSuccess = this.props.onSuccess;
            // 父组件传过来的成功后执行的方法，一般是列表刷新
            onSuccess && onSuccess(result);
            // 关闭弹窗
            this.handleCancel();
        };

        handleCancelConfirmLoading = () => {
            this.setState({confirmLoading: false});
        };

        handleNewCancel = () => {
            if (_.isFunction(customCancelHandler)) {
                customCancelHandler().then(() => this.handleCancel());
            }
            else {
                this.handleCancel();
            }
        };

        render() {
            const {open, confirmLoading} = this.state;

            const defaultPrefixCls = this.context.prefixCls;
            const prefixCls = customizePrefixCls
                || (defaultPrefixCls ? defaultPrefixCls + '-create-drawer' : '')
                || CLS_PREFIX;

            // footer配置
            let footerContent: React.ReactElement | null = null;
            if (foot !== false) {
                footerContent = (
                    <div className={`${prefixCls}-footer`}>
                        <Button key="cancel" onClick={this.handleNewCancel}>{cancelText || cancel}</Button>
                        <Button
                            key="ok"
                            type="primary"
                            disabled={confirmLoading}
                            loading={confirmLoading}
                            onClick={this.handleOk}
                        >
                            {confirmLoading ? submitting : (okText || sure)}
                        </Button>
                    </div>
                );
            }

            // 核心区域
            let main: React.ReactElement | null = null;
            if (WrappedComponent) {
                main = (
                    <WrappedComponent
                        wrappedComponentRef={this.saveRef}
                        ref={this.commonFormRef}
                        payload={payload}
                        onSuccess={this.handleFormSuccess}
                        onCancel={this.handleNewCancel}
                        onCancelConfirmLoading={this.handleCancelConfirmLoading}
                    />
                );
            }

            const drawerClass = classNames(className, prefixCls);

            const drawerComp = (
                <Drawer
                    className={drawerClass}
                    title={title}
                    width={width}
                    onClose={this.handleNewCancel}
                    visible={open}
                    destroyOnClose
                    maskClosable={maskClosable !== false}
                    mask={mask}
                    {...acudProps}
                >
                    {main}
                    {footerContent}
                </Drawer>
            );
            return createPortal(
                drawerComp,
                this.el
            );
        }
    };
};

export default createDrawer;
